// La banque : emprunter sans attendre une carte. Pendant les études en école privée, un prêt étudiant
// en différé total (rien à payer avant le premier emploi, mais les intérêts s'ajoutent à la dette) ;
// une fois au travail, un prêt personnel, refusé si les remboursements de crédit dépassaient 35 % du salaire.
import type { TextKey } from '../content/fr';
import { loanMonthlyPayment, round2 } from '../engine/finance';
import { fmtEur } from '../engine/format';
import type { GameState } from '../engine/state';
import { addJournal, addLoan } from './actions';
import { deferredDebt, inPrivateSchool, monthlyLoanPayments, monthsUntilFirstJob, studentLoansTotal } from './economy';

export const BANK = {
  student: { rate: 0.02, months: 60, min: 1000, max: 10000, cap: 25000 },
  personal: { rate: 0.06, min: 1000, max: 20000, durations: [12, 24, 36, 48, 60] },
  /** Les montants se choisissent par tranches de 1 000 €. */
  step: 1000,
  /** Les remboursements de crédit ne doivent pas dépasser cette part du salaire. */
  maxDebtRatio: 0.35,
};

export type BankLoanKind = 'student' | 'personal';

/** Le prêt que la banque propose en ce moment : étudiant à l'école privée, personnel une fois au travail. */
export function bankLoanKind(state: GameState): BankLoanKind | null {
  if (inPrivateSchool(state)) return 'student';
  return state.firstJobDay !== null ? 'personal' : null;
}

export interface BankLoanPreview {
  /** La dette au premier remboursement : pour un prêt étudiant, grossie des intérêts des études. */
  debt: number;
  payment: number;
  months: number;
  total: number;
  interest: number;
}

/** Ce que coûterait un prêt : la mensualité, le total remboursé et les intérêts. */
export function bankLoanPreview(state: GameState, kind: BankLoanKind, amount: number, months: number): BankLoanPreview {
  const rate = kind === 'student' ? BANK.student.rate : BANK.personal.rate;
  const duration = kind === 'student' ? BANK.student.months : months;
  const debt = kind === 'student' ? deferredDebt(amount, rate, monthsUntilFirstJob(state)) : amount;
  const payment = loanMonthlyPayment(debt, rate, duration);
  return {
    debt: round2(debt),
    payment: round2(payment),
    months: duration,
    total: round2(payment * duration),
    interest: round2(payment * duration - amount),
  };
}

/** Pourquoi la banque refuserait ce prêt, ou null si elle l'accorde. */
export function bankLoanRefusal(
  state: GameState,
  kind: BankLoanKind,
  amount: number,
  months: number,
): { key: TextKey; params: Record<string, string> } | null {
  if (state.ficp) return { key: 'bank.refuseFicp', params: {} };
  if (kind === 'student') {
    const already = studentLoansTotal(state);
    if (already + amount <= BANK.student.cap) return null;
    return { key: 'bank.refuseStudentCap', params: { cap: fmtEur(BANK.student.cap), total: fmtEur(already) } };
  }
  const payments = monthlyLoanPayments(state) + bankLoanPreview(state, kind, amount, months).payment;
  const limit = state.salary * BANK.maxDebtRatio;
  if (payments <= limit) return null;
  return { key: 'bank.refuseDebtRatio', params: { payments: fmtEur(payments), limit: fmtEur(limit) } };
}

/** Signer le prêt : l'argent arrive sur le compte courant. */
export function takeBankLoan(state: GameState, kind: BankLoanKind, amount: number, months: number): void {
  if (bankLoanKind(state) !== kind || bankLoanRefusal(state, kind, amount, months)) return;
  state.cash = round2(state.cash + amount);
  if (kind === 'student') {
    addLoan(state, 'loan.etudiantBanque', amount, BANK.student.rate, BANK.student.months, true);
    addJournal(state, 'journal.bankStudentLoan', { amount: fmtEur(amount) });
    return;
  }
  const loan = addLoan(state, 'loan.personnel', amount, BANK.personal.rate, months);
  addJournal(state, 'journal.bankLoan', { amount: fmtEur(amount), payment: fmtEur(loan.monthlyPayment), months });
}
